import type { Layer } from "@geosoft/viewer-sdk";
import type {
  CandidateFact,
  IntegrationOperationalState,
  SceneGeometryRef,
  UnifiedSceneTreeNode,
} from "./scenePackage";

type JsonObject = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Tool-card-family taxonomy (WP-2 foundation for WPs 3..10)
// ---------------------------------------------------------------------------

/**
 * Stable layer-family identifier used by the right-rail `ToolCardDock`
 * to pick which contextual tool card to render. The list is the
 * canonical eleven cards from
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §6.5 plus the
 * `gtwin` project / package family from §7.11.
 *
 * Cards 5..10 (`raster`, `terrain`, `vector`, `pointcloud`, `bim`,
 * `3dtiles`, `geophysics`, `sensor`, `ai`, `readiness`) read this kind
 * from the active `SceneSelection.toolCardKind` and decide whether
 * they should activate.
 */
export type ToolCardKind =
  | "basemap"
  | "raster"
  | "terrain"
  | "vector"
  | "pointcloud"
  | "bim"
  | "3dtiles"
  | "geophysics"
  | "sensor"
  | "ai"
  | "readiness"
  | "environment"
  | "gtwin";

/**
 * Compute the right-rail tool-card family for a given scene-tree node
 * or renderable layer. Pure function so future ToolCards (WPs 3..10),
 * the `SceneTreePanel` contextual menu, deep-link dispatchers, and
 * tests share one source of truth.
 *
 * Resolution order:
 *   1. Explicit `layerKind` from `@geosoft/viewer-sdk` Layer (or the
 *      scene-package `SceneItem.layerKind`).
 *   2. Scene-index node `kind` for index-only nodes (e.g. `readiness`
 *      gap nodes, `evidence`, `integration`, `ai-candidate`).
 *   3. Default `gtwin` (project / package card) when nothing else
 *      matches — never returns `undefined`.
 */
export function getToolCardKindFor(
  input:
    | Layer
    | UnifiedSceneTreeNode
    | { readonly layerKind?: string; readonly indexNodeKind?: string },
): ToolCardKind {
  const gtwinSlot = readGtwinSlot(input);
  if (gtwinSlot === "terrain") return "terrain";

  const layerKind = readLayerKind(input);
  const indexNodeKind = readIndexNodeKind(input);
  const fromLayer = layerKind ? mapLayerKind(layerKind) : null;
  if (fromLayer) return fromLayer;
  const fromNode = indexNodeKind ? mapIndexNodeKind(indexNodeKind) : null;
  if (fromNode) return fromNode;
  return "gtwin";
}

function readGtwinSlot(input: unknown): string | undefined {
  if (!input || typeof input !== "object") return undefined;
  const record = input as Record<string, unknown>;
  const style = record.style;
  if (!style || typeof style !== "object") return undefined;
  const slot = (style as Record<string, unknown>).gtwinSlot;
  return typeof slot === "string" ? slot : undefined;
}

function readLayerKind(input: unknown): string | undefined {
  if (!input || typeof input !== "object") return undefined;
  const record = input as Record<string, unknown>;
  if (typeof record.layerKind === "string") return record.layerKind;
  if (typeof record.kind === "string" && "endpoints" in record) {
    return record.kind;
  }
  return undefined;
}

function readIndexNodeKind(input: unknown): string | undefined {
  if (!input || typeof input !== "object") return undefined;
  const record = input as Record<string, unknown>;
  if (typeof record.indexNodeKind === "string") return record.indexNodeKind;
  if (
    typeof record.kind === "string" &&
    !("endpoints" in record) &&
    "sceneItemIds" in record
  ) {
    return record.kind;
  }
  return undefined;
}

function mapLayerKind(kind: string): ToolCardKind | null {
  switch (kind) {
    case "basemap":
    case "tile":
    case "vt-style":
      return "basemap";
    case "raster":
    case "cog":
    case "pmtiles-raster":
    case "mosaic-json":
      return "raster";
    case "terrain":
    case "dem-contours":
    case "quantized-mesh":
      return "terrain";
    case "vector":
    case "pmtiles":
    case "geojson":
    case "flatgeobuf":
    case "geoparquet":
    case "dxf":
    case "ogc-features-snapshot":
      return "vector";
    case "pointcloud":
    case "copc":
    case "potree-v2-octree":
      return "pointcloud";
    case "bim":
    case "glb":
      return "bim";
    case "3dtiles":
    case "cityjson-3dtiles":
      return "3dtiles";
    case "geophysics":
    case "sidescan":
    case "bathymetry":
      return "geophysics";
    case "sensor":
    case "timeseries":
    case "sensor/timeseries":
    case "sensor-stream-frame-v1":
      return "sensor";
    case "panorama":
    case "panorama-pose-v1":
    case "panorama-bubble":
      return "sensor";
    case "environment":
      return "environment";
    default:
      return null;
  }
}

function mapIndexNodeKind(kind: string): ToolCardKind | null {
  if (kind.startsWith("readiness")) return "readiness";
  if (kind.startsWith("ai") || kind === "evidence" || kind === "candidate")
    return "ai";
  if (kind === "scene" || kind === "group" || kind === "root") return "gtwin";
  // WP-8 — `integration` index nodes drive the Sensor / live overlay
  // ToolCard per `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
  // §6.5 Card 8 ("`sensor/timeseries` ... and live `integration`
  // nodes"). Selecting an integration node opens the live-data card,
  // not the gTwin project card.
  if (kind === "integration") return "sensor";
  // Fall through to layer-kind resolution for kinds the index reuses
  // verbatim from `SCENE_CONTRACT.md` (e.g. `pointcloud`, `raster`).
  return mapLayerKind(kind);
}

export interface SceneCatalogGroup {
  readonly id: string;
  readonly label: string;
  readonly slot: string;
  readonly semanticKind: string;
  readonly layers: readonly Layer[];
}

export interface SceneSearchResult {
  readonly id: string;
  readonly label: string;
  readonly kind: "node" | "layer";
  readonly detail: string;
  readonly node?: UnifiedSceneTreeNode;
  readonly layer?: Layer;
}

export interface SemanticPropertyFact {
  readonly label: string;
  readonly value: string;
}

export interface SemanticPropertyLink {
  readonly label: string;
  readonly detail: string;
}

export interface SemanticPropertySummary {
  readonly facts: readonly SemanticPropertyFact[];
  readonly candidateFacts: readonly CandidateFact[];
  readonly links: readonly SemanticPropertyLink[];
  readonly geometryRefs: readonly SceneGeometryRef[];
  readonly integrationStates: readonly IntegrationOperationalState[];
}

export function slotLabel(slot: string): string {
  switch (slot) {
    case "basemaps":
      return "Base maps";
    case "terrain":
      return "Terrain";
    case "realityData":
      return "Reality data";
    case "engineeringModels":
      return "Engineering models";
    case "featureLayers":
      return "Feature layers";
    case "overlays":
      return "Overlays";
    case "environment":
      return "Environment";
    default:
      return "Project";
  }
}

export function semanticKindLabel(kind: string): string {
  switch (kind) {
    case "3dtiles":
      return "3D Tiles";
    case "bim":
      return "Engineering model";
    case "pointcloud":
      return "Point cloud";
    case "raster":
      return "Raster and terrain imagery";
    case "terrain":
      return "Terrain surface";
    case "vector":
      return "Feature data";
    case "sensor":
    case "timeseries":
      return "Operational data";
    case "panorama-bubble":
      return "Panoramas (Bubble View)";
    case "environment":
      return "Weather & atmosphere";
    default:
      return kind;
  }
}

export function flattenSceneNodes(
  tree: UnifiedSceneTreeNode | null | undefined,
): UnifiedSceneTreeNode[] {
  const nodes: UnifiedSceneTreeNode[] = [];
  const visit = (node: UnifiedSceneTreeNode) => {
    nodes.push(node);
    for (const child of node.children) visit(child);
  };
  if (tree) visit(tree);
  return nodes;
}

export function filterSceneTree(
  tree: UnifiedSceneTreeNode,
  query: string,
): UnifiedSceneTreeNode | null {
  const normalized = normalizeQuery(query);
  if (!normalized) return tree;
  const visit = (node: UnifiedSceneTreeNode): UnifiedSceneTreeNode | null => {
    const children = node.children
      .map(visit)
      .filter((child): child is UnifiedSceneTreeNode => Boolean(child));
    if (sceneNodeMatchesQuery(node, normalized) || children.length > 0) {
      return { ...node, children };
    }
    return null;
  };
  return visit(tree);
}

export function sceneNodeMatchesQuery(
  node: UnifiedSceneTreeNode,
  query: string,
): boolean {
  const q = normalizeQuery(query);
  if (!q) return true;
  return [
    node.id,
    node.label,
    node.kind,
    node.intelligenceLevel,
    ...node.sceneItemIds,
    ...node.readiness,
    ...node.geometryRefs.flatMap((ref) => [
      ref.sceneItemId,
      ref.featureId,
      ref.contentUri,
      ref.sourceFeatureId == null ? undefined : String(ref.sourceFeatureId),
    ]),
    ...node.integrationStates.flatMap((state) => [
      state.label,
      state.state,
      state.providerType,
      state.status,
      state.permissionState,
    ]),
    ...(node.candidateFacts ?? []).flatMap((fact) => [
      fact.predicate,
      fact.literalValue,
      fact.objectNodeId,
      fact.factType,
      fact.source,
      fact.reviewStatus,
    ]),
  ].some(
    (value) => typeof value === "string" && value.toLowerCase().includes(q),
  );
}

export function layerMatchesCatalogQuery(
  layer: Layer,
  query: string,
  slot?: string,
): boolean {
  const q = normalizeQuery(query);
  if (!q) return true;
  return [
    layer.id,
    layer.label,
    layer.kind,
    layer.crs,
    slot,
    ...Object.values(layer.endpoints),
    ...(layer.derivedFrom ?? []),
  ].some(
    (value) => typeof value === "string" && value.toLowerCase().includes(q),
  );
}

export function buildCatalogGroups(
  layers: readonly Layer[],
  slotMap: Readonly<Record<string, string>>,
  query = "",
): SceneCatalogGroup[] {
  const groups = new Map<
    string,
    { slot: string; semanticKind: string; layers: Layer[] }
  >();
  for (const layer of layers) {
    const slot = slotMap[layer.id] ?? "project";
    if (!layerMatchesCatalogQuery(layer, query, slot)) continue;
    const semanticKind = semanticKindLabel(layer.kind);
    const key = `${slot}:${semanticKind}`;
    const existing = groups.get(key) ?? { slot, semanticKind, layers: [] };
    existing.layers.push(layer);
    groups.set(key, existing);
  }
  return [...groups.entries()].map(([id, group]) => ({
    id,
    label: `${slotLabel(group.slot)} / ${group.semanticKind}`,
    slot: group.slot,
    semanticKind: group.semanticKind,
    layers: group.layers,
  }));
}

export function buildLocalSearchResults(input: {
  readonly tree?: UnifiedSceneTreeNode | null;
  readonly layers: readonly Layer[];
  readonly slotMap: Readonly<Record<string, string>>;
  readonly query: string;
  readonly limit?: number;
}): SceneSearchResult[] {
  const q = normalizeQuery(input.query);
  if (!q) return [];
  const limit = input.limit ?? 8;
  const results: SceneSearchResult[] = [];
  for (const node of flattenSceneNodes(input.tree)) {
    if (!sceneNodeMatchesQuery(node, q)) continue;
    results.push({
      id: `node:${node.id}`,
      label: node.label,
      kind: "node",
      detail: `${node.kind}${node.sceneItemIds.length ? ` / ${node.sceneItemIds.join(", ")}` : ""}`,
      node,
    });
    if (results.length >= limit) return results;
  }
  for (const layer of input.layers) {
    const slot = input.slotMap[layer.id] ?? "project";
    if (!layerMatchesCatalogQuery(layer, q, slot)) continue;
    results.push({
      id: `layer:${layer.id}`,
      label: layer.label ?? layer.id,
      kind: "layer",
      detail: `${slotLabel(slot)} / ${semanticKindLabel(layer.kind)} / ${layer.crs}`,
      layer,
    });
    if (results.length >= limit) return results;
  }
  return results;
}

export function firstLayerForSceneNode(
  node: UnifiedSceneTreeNode | null | undefined,
  layers: readonly Layer[],
): Layer | null {
  if (!node) return null;
  const byId = new Map(layers.map((layer) => [layer.id, layer]));
  for (const sceneItemId of node.sceneItemIds) {
    const layer = byId.get(sceneItemId);
    if (layer) return layer;
  }
  for (const ref of node.geometryRefs) {
    if (!ref.sceneItemId) continue;
    const layer = byId.get(ref.sceneItemId);
    if (layer) return layer;
  }
  return null;
}

export function summarizeSceneNode(
  node: UnifiedSceneTreeNode,
  layer: Layer | null,
): SemanticPropertySummary {
  const facts: SemanticPropertyFact[] = [
    { label: "Name", value: node.label },
    { label: "Type", value: node.kind },
  ];
  addFact(facts, "Scene node ID", node.id);
  addFact(facts, "Source layer", layer?.label ?? layer?.id);
  addFact(facts, "Scene item refs", node.sceneItemIds.join(", "));
  addFact(facts, "Readiness", node.readiness.join(", "));
  addFact(facts, "Intelligence", node.intelligenceLevel);

  const attributes = asObject(node.attributes);
  const extensions = asObject(attributes?.extensions);
  addFact(
    facts,
    "Provenance",
    readString(attributes?.provenance) ??
      readString(attributes?.source) ??
      readString(layer?.style?.source),
  );
  const confidence =
    readNumber(attributes?.confidence) ??
    readNumber(attributes?.aiConfidence) ??
    readNumber(layer?.style?.confidence);
  if (confidence != null)
    addFact(facts, "Confidence", `${Math.round(confidence * 100)}%`);
  addFact(
    facts,
    "Trusted state",
    readString(attributes?.trusted) ?? readString(attributes?.trustState),
  );
  addFact(
    facts,
    "Review state",
    readString(attributes?.reviewState) ?? readString(attributes?.status),
  );

  const links = [
    ...readLinks(attributes?.documents, "Document"),
    ...readLinks(attributes?.artifacts, "Artifact"),
    ...readLinks(attributes?.sensors, "Sensor"),
    ...readLinks(attributes?.integrations, "Integration"),
    ...readLinks(extensions?.documents, "Document"),
    ...readLinks(extensions?.artifacts, "Artifact"),
    ...readLinks(extensions?.sensors, "Sensor"),
    ...readLinks(extensions?.integrations, "Integration"),
  ];

  return {
    facts,
    candidateFacts: node.candidateFacts ?? [],
    links,
    geometryRefs: node.geometryRefs,
    integrationStates: node.integrationStates,
  };
}

function addFact(
  facts: SemanticPropertyFact[],
  label: string,
  value: unknown,
): void {
  if (typeof value === "string" && value.trim().length > 0) {
    facts.push({ label, value });
  }
}

function readLinks(
  value: unknown,
  fallbackLabel: string,
): SemanticPropertyLink[] {
  const entries = Array.isArray(value) ? value : value ? [value] : [];
  return entries.map((entry, index) => {
    if (typeof entry === "string")
      return { label: fallbackLabel, detail: entry };
    const record = asObject(entry);
    return {
      label:
        readString(record?.label) ??
        readString(record?.type) ??
        `${fallbackLabel} ${index + 1}`,
      detail:
        readString(record?.href) ??
        readString(record?.id) ??
        readString(record?.name) ??
        "Linked reference",
    };
  });
}

function normalizeQuery(query: string): string {
  return query.trim().toLowerCase();
}

function asObject(value: unknown): JsonObject | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as JsonObject)
    : null;
}

function readString(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (typeof value === "boolean") return value ? "Trusted" : "Untrusted";
  return null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
